/**
 * Frontend Sentry init. No-op unless `VITE_SENTRY_DSN` is provided at build
 * time, so dev builds and any deployment without a DSN are unaffected.
 *
 * Sentry (≈90 Ko compressés avec le suivi de performance) n'est plus dans le
 * bundle initial : il est importé dynamiquement une fois l'interface affichée,
 * quand le navigateur est au repos. Contrepartie assumée : une erreur levée
 * dans les premières centaines de millisecondes n'est pas remontée.
 *
 * Importé pour son effet de bord depuis `main.tsx`.
 */
const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;

export const sentryEnabled = Boolean(dsn);

type SentryModule = typeof import('@sentry/react');
let loading: Promise<SentryModule> | null = null;

/** Charge et initialise Sentry une seule fois. */
export function loadSentry(): Promise<SentryModule> {
  loading ??= import('@sentry/react').then((Sentry) => {
    Sentry.init({
      dsn,
      environment: (import.meta.env.VITE_SENTRY_ENVIRONMENT as string) || import.meta.env.MODE,
      release: import.meta.env.VITE_APP_VERSION as string | undefined,
      integrations: [Sentry.browserTracingIntegration()],
      // Échantillonnage perf actif par défaut (10 %) : sans lui, les Web Vitals
      // sont collectés puis jetés et personne ne mesure rien en production.
      // Reste surchargeable par VITE_SENTRY_TRACES_SAMPLE_RATE.
      tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? '0.1'),
      // Rattache les traces front aux traces API (même origine `/api`).
      tracePropagationTargets: [/^\/api/],
    });
    return Sentry;
  });
  return loading;
}

/** Remonte une exception ; sans DSN, ne charge rien. */
export function captureException(error: unknown, context?: { react?: { componentStack: string } }): void {
  if (!sentryEnabled) return;
  void loadSentry().then((Sentry) => {
    Sentry.withScope((scope) => {
      if (context?.react) scope.setContext('react', context.react);
      Sentry.captureException(error);
    });
  });
}

if (sentryEnabled && typeof window !== 'undefined') {
  const start = () => void loadSentry();
  if ('requestIdleCallback' in window) {
    (window as Window & { requestIdleCallback: (cb: () => void, opts?: { timeout: number }) => void })
      .requestIdleCallback(start, { timeout: 4000 });
  } else {
    setTimeout(start, 2000);
  }
}
